import { Event, SportType } from '../types/event';
import axios, { InternalAxiosRequestConfig } from 'axios';

// Get API URL from environment variable
let API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Ensure the URL is absolute (starts with http:// or https://)
if (API_BASE_URL && !API_BASE_URL.startsWith('http://') && !API_BASE_URL.startsWith('https://')) {
  // If it's missing the protocol, add https://
  API_BASE_URL = `https://${API_BASE_URL}`;
}

// Remove trailing slash if present
API_BASE_URL = API_BASE_URL.replace(/\/$/, '');


export interface User {
  id: string;
  email?: string; // Optional because public profiles don't include email
  display_name: string;
  profile_picture?: string | null;
  bio?: string | null;
  gallery_image_1?: string | null;
  gallery_image_2?: string | null;
  gallery_image_3?: string | null;
  gallery_image_4?: string | null;
  created_at?: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
  birthday?: string;
  sports?: Array<{
    sport_name: string;
    skill_level: string;
    preferences?: Array<{ id: string; name: string }>;
  }>;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  display_name: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user_id: string;
  display_name: string;
  email: string;
}

export interface Tag {
  id: string;
  name: string;
  color?: string;
  description?: string;
}

export interface BackendEvent {
  id: string;
  title: string;
  description?: string;
  capacity: number;
  starts_at: string;
  time?: string; // For backward compatibility
  activity_type?: string;
  sport_type?: string; // For backward compatibility
  location?: string;
  address?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  created_by: string;
  host_name?: string;
  host_profile_picture?: string | null;
  available_spots?: number;
  occupied_spots?: number;
  level_needed?: string; // Deprecated: use level_requirement instead
  level_requirement?: string; // Level ID: "level_1" to "level_5"
  images?: {
    image_1?: string | null;
    image_2?: string | null;
    image_3?: string | null;
  };
  tags?: Array<{
    id: string;
    name: string;
    color?: string;
    description?: string;
  }>;
  auto_accept?: boolean; // Added for free-join UI
  status?: 'ACTIVE' | 'CANCELED';
  cancellation_deadline_hours?: number;
  like_count?: number;
  is_liked_by_user?: boolean;
}

export interface EventParticipant {
  user_id: string;
  display_name: string;
  profile_picture?: string | null;
  role: 'host' | 'guest';
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to convert backend event to frontend event format
const convertBackendEventToFrontend = (backendEvent: BackendEvent): Event => {
  // Extract sport type from activity_type or tags
  let sportType: SportType = 'Tennis'; // default

  if (backendEvent.activity_type) {
    // Map activity types to sport types
    const activityToSport: { [key: string]: SportType } = {
      'tennis': 'Tennis',
      'badminton': 'Badminton',
      'golf': 'Golf',
      'bouldering': 'Bouldering',
      'pickleball': 'Pickleball',
      'gym': 'Gym',
      'hiking': 'Hiking',
      'skiing': 'Ski/Snowboarding',
      'snowboarding': 'Ski/Snowboarding'
    };
    sportType = activityToSport[backendEvent.activity_type.toLowerCase()] || 'Tennis';
  } else if (backendEvent.tags && backendEvent.tags.length > 0) {
    // Try to infer sport from tags
    const sportTag = backendEvent.tags.find(tag =>
      ['Tennis', 'Badminton', 'Golf', 'Bouldering', 'Pickleball', 'Gym', 'Hiking'].includes(tag.name)
    );
    if (sportTag) {
      sportType = sportTag.name as SportType;
    }
  }

  // Determine level from tags
  let level = 'Any Level';
  const levelTag = backendEvent.tags?.find(tag =>
    ['Beginner', 'Intermediate', 'Advanced'].includes(tag.name)
  );
  if (levelTag) {
    level = levelTag.name;
  }

  return {
    id: backendEvent.id,
    title: backendEvent.title,
    sport_type: sportType,
    time: backendEvent.starts_at,
    starts_at: backendEvent.starts_at, // Required field
    capacity: backendEvent.capacity, // Required field
    location: backendEvent.location || backendEvent.address || 'Location TBD',
    address: backendEvent.address,
    formatted_address: backendEvent.formatted_address,
    latitude: backendEvent.latitude,
    longitude: backendEvent.longitude,
    activity_type: backendEvent.activity_type,
    level_needed: backendEvent.level_needed || level, // Deprecated
    level_requirement: backendEvent.level_requirement,
    available_spots: backendEvent.available_spots || backendEvent.capacity,
    occupied_spots: backendEvent.occupied_spots || 0,
    host_name: backendEvent.host_name || 'Host',
    host_profile_picture: backendEvent.host_profile_picture ?? undefined,
    description: backendEvent.description || '',
    created_by: backendEvent.created_by,
    auto_accept: (backendEvent as any).auto_accept === true,
    status: backendEvent.status as 'ACTIVE' | 'CANCELED' || 'ACTIVE',
    cancellation_deadline_hours: backendEvent.cancellation_deadline_hours || 24,
    images: backendEvent.images,
    tags: backendEvent.tags,
    like_count: backendEvent.like_count,
    is_liked_by_user: backendEvent.is_liked_by_user
  };
};

export const eventApi = {
  getEvents: async (activityFilter?: string, tagFilter?: string, levelFilter?: string): Promise<Event[]> => {
    try {
      const params = new URLSearchParams();

      // Add activity filter (sport name) if provided
      if (activityFilter) {
        params.append('activity_filter', activityFilter);
      }

      // Add tag filter if provided
      if (tagFilter) {
        params.append('tag_filter', tagFilter);
      }

      // Add level filter if provided
      if (levelFilter) {
        params.append('level_filter', levelFilter);
      }

      const url = params.toString() ? `/events?${params.toString()}` : '/events';
      const response = await api.get<BackendEvent[]>(url);
      return response.data.map(convertBackendEventToFrontend);
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  getEventById: async (eventId: string): Promise<Event> => {
    try {
      const response = await api.get<BackendEvent>(`/events/${eventId}`);
      return convertBackendEventToFrontend(response.data);
    } catch (error) {
      console.error('Error fetching event by ID:', error);
      throw error;
    }
  },

  getEventParticipants: async (eventId: string): Promise<EventParticipant[]> => {
    try {
      const response = await api.get<EventParticipant[]>(`/events/${eventId}/participants`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event participants:', error);
      throw error;
    }
  },

  createEvent: async (eventData: {
    title: string;
    description?: string;
    starts_at: string;
    capacity?: number;
    activity_type?: string;
    location?: string;
    address?: string;
    formatted_address?: string;
    latitude?: number;
    longitude?: number;
    tag_ids?: string[];
    cancellation_deadline_hours?: number;
    level_requirement?: string; // Level ID: "level_1" to "level_5"
  }): Promise<BackendEvent> => {
    try {
      const response = await api.post<BackendEvent>('/events', eventData);
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  updateEvent: async (eventId: string, eventData: {
    title?: string;
    description?: string;
    starts_at?: string;
    capacity?: number;
    activity_type?: string;
    location?: string;
    address?: string;
    formatted_address?: string;
    latitude?: number;
    longitude?: number;
    image_1?: string | null;
    image_2?: string | null;
    image_3?: string | null;
    tag_ids?: string[];
    cancellation_deadline_hours?: number;
    level_requirement?: string; // Level ID: "level_1" to "level_5"
  }): Promise<{ message: string; event: BackendEvent }> => {
    try {
      // Validate image data URLs before sending
      const imageFields = ['image_1', 'image_2', 'image_3'] as const;
      for (const field of imageFields) {
        const imageData = eventData[field];
        if (imageData && typeof imageData === 'string') {
          // Check if it's a data URL and validate MIME type
          if (imageData.startsWith('data:image/')) {
            const mimeType = imageData.split(',')[0];
            // Reject HEIC/HEIF explicitly
            if (mimeType.includes('heic') || mimeType.includes('heif')) {
              throw new Error(`${field}: HEIC images are not supported. Please convert to JPEG first.`);
            }
            // Ensure it's a supported format
            if (!mimeType.match(/data:image\/(jpeg|jpg|png|gif|webp)/i)) {
              throw new Error(`${field}: Unsupported image format. Only JPEG, PNG, GIF, and WebP are allowed.`);
            }
          }
        }
      }
      
      const response = await api.put(`/events/${eventId}`, eventData);
      return response.data;
    } catch (error: any) {
      // Log full error details for debugging
      if (error.response) {
        console.error('Error updating event - Status:', error.response.status);
        console.error('Error updating event - Response:', error.response.data);
        console.error('Error updating event - Detail:', error.response.data?.detail);
      } else {
        console.error('Error updating event:', error);
      }
      throw error;
    }
  },

  uploadEventImages: async (eventId: string, images: {
    image_1?: string | null;
    image_2?: string | null;
    image_3?: string | null;
  }): Promise<{ message: string }> => {
    try {
      const response = await api.post(`/events/${eventId}/images`, images);
      return response.data;
    } catch (error) {
      console.error('Error uploading event images:', error);
      throw error;
    }
  },

  cancelEvent: async (eventId: string): Promise<void> => {
    try {
      await api.post(`/events/${eventId}/cancel`);
    } catch (error) {
      console.error('Error canceling event:', error);
      throw error;
    }
  },

  getMyEvents: async (): Promise<Event[]> => {
    try {
      const response = await api.get<BackendEvent[]>('/events/my');
      return response.data.map(convertBackendEventToFrontend);
    } catch (error) {
      console.error('Error fetching my events:', error);
      throw error;
    }
  },

  likeEvent: async (eventId: string): Promise<{ message: string; like_count: number }> => {
    const response = await api.post(`/events/${eventId}/like`);
    return response.data;
  },

  unlikeEvent: async (eventId: string): Promise<{ message: string; like_count: number }> => {
    const response = await api.delete(`/events/${eventId}/like`);
    return response.data;
  },

  getLikedEvents: async (): Promise<Event[]> => {
    const response = await api.get<BackendEvent[]>('/events/liked');
    return response.data.map(convertBackendEventToFrontend);
  }
};

export const authApi = {
  login: async (loginData: LoginData): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', {
      email: loginData.email,
      password: loginData.password
    });

    return response.data;
  },

  register: async (registerData: RegisterData): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', {
      email: registerData.email,
      password: registerData.password,
      display_name: registerData.display_name
    });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    localStorage.removeItem('access_token');
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (email: string, code: string, newPassword: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/reset-password', {
      email,
      code,
      new_password: newPassword,
    });
    return response.data;
  }
};

export interface UserRequest {
  id: string;
  event_id: string;
  user_id: string;
  host_id: string;
  status: 'SUBMITTED' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CANCELED';
  user_name: string;
  host_name: string;
  event_title: string;
  created_at: string;
}

export const requestApi = {
  getUserRequests: async (): Promise<UserRequest[]> => {
    try {
      const response = await api.get<UserRequest[]>('/requests/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching user requests:', error);
      throw error;
    }
  },

  createRequest: async (eventId: string, autoAccept: boolean = false): Promise<{
    request_id: string;
    thread_id: string;
    status: string;
  }> => {
    try {
      const response = await api.post('/requests', {
        event_id: eventId,
        host_id: "", // Backend will use event.created_by instead
        auto_accept: autoAccept
      });
      return response.data;
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  },

  acceptRequest: async (requestId: string): Promise<{
    status: string;
    booking_id: string;
    thread_id: string;
  }> => {
    try {
      const response = await api.post(`/requests/${requestId}/act`, {
        action: 'accept'
      });
      return response.data;
    } catch (error) {
      console.error('Error accepting request:', error);
      throw error;
    }
  },

  declineRequest: async (requestId: string): Promise<{
    status: string;
    thread_id: string;
  }> => {
    try {
      const response = await api.post(`/requests/${requestId}/act`, {
        action: 'decline'
      });
      return response.data;
    } catch (error) {
      console.error('Error declining request:', error);
      throw error;
    }
  },

  cancelRequest: async (requestId: string): Promise<{
    message: string;
    status: string;
  }> => {
    try {
      const response = await api.post(`/requests/${requestId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error canceling request:', error);
      throw error;
    }
  }
};

export const tagApi = {
  getTags: async (sport?: string): Promise<Array<{
    id: string;
    name: string;
    sport_type?: string;
    color?: string;
    description?: string;
  }>> => {
    try {
      const params = sport ? `?sport=${encodeURIComponent(sport)}` : '';
      const response = await api.get(`/tags${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  },

  createTag: async (tagData: {
    name: string;
    sport_type?: string;
    color?: string;
    description?: string;
  }): Promise<{
    id: string;
    name: string;
    sport_type?: string;
    color?: string;
    description?: string;
  }> => {
    try {
      const response = await api.post('/tags', tagData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }
};

// Chat API Types
export interface ChatMessage {
  id: string;
  thread_id: string;
  sender_id: string | null;
  sender_name?: string;
  kind: 'user' | 'system';
  body: string;
  created_at: string;
  seq: number;
}

export interface EventSummary {
  id: string;
  title: string;
  description?: string;
  starts_at: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  activity_type?: string;
}

export interface LastMessage {
  id: string;
  sender_id: string | null;
  sender_name?: string;
  body: string;
  created_at: string;
  kind: 'user' | 'system';
}

export interface ThreadParticipant {
  thread_id: string;
  user_id: string;
  user_name: string;
  role: 'host' | 'guest';
}

export interface ChatThread {
  id: string;
  scope: 'request' | 'booking';
  event_id: string;
  is_locked: boolean;
  created_at: string;
  event?: EventSummary;
  last_message?: LastMessage;
  unread_count: number;
  participant_count: number;
}

export interface ThreadListResponse {
  threads: ChatThread[];
  participants: { [threadId: string]: ThreadParticipant[] };
}

export interface ThreadDetails {
  thread: {
    id: string;
    scope: 'request' | 'booking';
    is_locked: boolean;
    event_id: string;
    created_at: string;
  };
  event: {
    id: string;
    title: string;
    description?: string;
    starts_at: string;
    location?: string;
    address?: string;
    formatted_address?: string;
    latitude?: number;
    longitude?: number;
    activity_type?: string;
    capacity: number;
    created_by: string;
    creator_name: string;
    tags: Array<{
      id: string;
      name: string;
      color?: string;
      description?: string;
    }>;
  };
  requests: Array<{
    id: string;
    status: string;
    guest_id: string;
    guest_name: string;
    host_id: string;
    host_name: string;
    created_at: string;
    booking?: {
      id: string;
      status: string;
    };
  }>;
}

export const chatApi = {
  // Get all user's chat threads
  getThreads: async (): Promise<ThreadListResponse> => {
    try {
      const response = await api.get<ThreadListResponse>('/threads');
      return response.data;
    } catch (error) {
      console.error('Error fetching threads:', error);
      throw error;
    }
  },

  // Get messages for a specific thread
  getMessages: async (threadId: string, limit: number = 50, offset: number = 0): Promise<ChatMessage[]> => {
    try {
      const response = await api.get<ChatMessage[]>(`/threads/${threadId}/messages`, {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  // Send a message to a thread
  sendMessage: async (threadId: string, body: string): Promise<ChatMessage> => {
    try {
      const clientMsgId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const response = await api.post<ChatMessage>(`/threads/${threadId}/messages`, {
        client_msg_id: clientMsgId,
        body
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  // Mark messages as read
  markMessagesRead: async (threadId: string, lastReadSeq: number): Promise<void> => {
    try {
      await api.post(`/threads/${threadId}/read`, {
        last_read_seq: lastReadSeq
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  },

  // Get thread details including event info
  getThreadDetails: async (threadId: string): Promise<ThreadDetails> => {
    try {
      const response = await api.get<ThreadDetails>(`/threads/${threadId}/details`);
      return response.data;
    } catch (error) {
      console.error('Error fetching thread details:', error);
      throw error;
    }
  },

  // Get thread participants
  getThreadParticipants: async (threadId: string): Promise<ThreadParticipant[]> => {
    try {
      const response = await api.get<ThreadParticipant[]>(`/threads/${threadId}/participants`);
      return response.data;
    } catch (error) {
      console.error('Error fetching thread participants:', error);
      throw error;
    }
  }
};

// User Profile API
export const userApi = {
  updateProfile: async (profileData: {
    display_name?: string;
    bio?: string;
    gallery_image_1?: string | null;
    gallery_image_2?: string | null;
    gallery_image_3?: string | null;
    gallery_image_4?: string | null;
    gallery_image_5?: string | null;
    gallery_image_6?: string | null;
    first_name?: string;
    last_name?: string;
    gender?: string;
    birthday?: string | null;
    sports?: Array<{
      sport_name: string;
      skill_level: string;
      preferences?: string[];
    }>;
  }): Promise<{ message: string; user: User }> => {
    try {
      const response = await api.put('/users/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  getUserProfile: async (userId: string): Promise<User> => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  getUserHostingEvents: async (userId: string): Promise<Event[]> => {
    try {
      const response = await api.get<BackendEvent[]>(`/users/${userId}/events`);
      return response.data.map(convertBackendEventToFrontend);
    } catch (error) {
      console.error('Error fetching user hosting events:', error);
      throw error;
    }
  }
};

// ---------------------
// Address / Location API (Radar via backend proxy)
// ---------------------
export interface AddressSuggestion {
  formattedAddress: string;
  addressLabel?: string;
  placeLabel?: string;
  city?: string;
  state?: string;
  stateCode?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  latitude: number;
  longitude: number;
  layer?: string;
}

export interface GeocodeResult {
  formattedAddress: string;
  addressLabel?: string;
  city?: string;
  state?: string;
  stateCode?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  latitude: number;
  longitude: number;
  confidence?: string;
}

export const addressApi = {
  /**
   * Autocomplete partial addresses or search places via Radar (proxied through backend).
   * Use for as-you-type search. For place search (e.g. "badminton" → nearby courts),
   * pass layers: 'place' and near: 'lat,lng' (from geolocation).
   */
  autocomplete: async (
    query: string,
    options?: { near?: string; limit?: number; country?: string; layers?: string }
  ): Promise<{ suggestions: AddressSuggestion[] }> => {
    try {
      const params: Record<string, string> = { query };
      if (options?.near) params.near = options.near;
      if (options?.limit) params.limit = String(options.limit);
      if (options?.country) params.country = options.country;
      if (options?.layers) params.layers = options.layers;

      const response = await api.get('/api/address/autocomplete', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      throw error;
    }
  },

  /**
   * Forward geocode a complete address string to coordinates.
   */
  geocode: async (
    query: string,
    country?: string
  ): Promise<{ result: GeocodeResult | null }> => {
    try {
      const params: Record<string, string> = { query };
      if (country) params.country = country;

      const response = await api.get('/api/address/geocode', { params });
      return response.data;
    } catch (error) {
      console.error('Error geocoding address:', error);
      throw error;
    }
  },

  /**
   * Search places near a location (Radar Places Search API).
   * Filter by categories (e.g. "sports-recreation", "gym") or chains.
   * Requires near (lat,lng). Either categories or chains must be provided.
   */
  searchPlaces: async (
    near: string,
    options: { categories?: string; chains?: string; limit?: number; radius?: number }
  ): Promise<{ suggestions: AddressSuggestion[] }> => {
    const { categories, chains, limit = 20, radius = 10000 } = options;
    if (!categories && !chains) {
      throw new Error('searchPlaces requires either categories or chains');
    }
    try {
      const params: Record<string, string> = { near };
      if (categories) params.categories = categories;
      if (chains) params.chains = chains;
      params.limit = String(limit);
      params.radius = String(radius);
      const response = await api.get('/api/address/places', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching places:', error);
      throw error;
    }
  },

  /**
   * Reverse geocode coordinates to an address.
   */
  reverseGeocode: async (
    latitude: number,
    longitude: number
  ): Promise<{ result: GeocodeResult | null }> => {
    try {
      const response = await api.get('/api/address/reverse-geocode', {
        params: { latitude, longitude }
      });
      return response.data;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      throw error;
    }
  },
};

// Notification API Types
export type NotificationType = 
  | 'event_join_request'
  | 'event_join_accepted'
  | 'event_join_declined'
  | 'event_joined'
  | 'event_left'
  | 'event_canceled'
  | 'new_message';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
  event_id?: string | null;
  thread_id?: string | null;
  request_id?: string | null;
  related_user_id?: string | null;
  metadata?: Record<string, any> | null;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unread_count: number;
  total_count: number;
}

export const notificationApi = {
  getNotifications: async (limit: number = 50, offset: number = 0, unreadOnly: boolean = false): Promise<NotificationListResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (unreadOnly) {
        params.append('unread_only', 'true');
      }
      const response = await api.get<NotificationListResponse>(`/notifications?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    try {
      const response = await api.get<{ unread_count: number }>('/notifications/unread-count');
      return response.data;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  markAsRead: async (notificationIds: string[]): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>('/notifications/mark-read', {
        notification_ids: notificationIds
      });
      return response.data;
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  },

  markSingleAsRead: async (notificationId: string): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>(`/notifications/${notificationId}/mark-read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  deleteNotification: async (notificationId: string): Promise<{ message: string }> => {
    try {
      const response = await api.delete<{ message: string }>(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  deleteAllNotifications: async (readOnly: boolean = false): Promise<{ message: string }> => {
    try {
      const params = readOnly ? '?read_only=true' : '';
      const response = await api.delete<{ message: string }>(`/notifications${params}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notifications:', error);
      throw error;
    }
  }
};
